<template>
    <div>
        <b-modal
            id="kyc-status-review-popup"
            ref="modal"
            title="KYC Status Review"
            size="sm"
            no-close-on-backdrop
            no-close-on-esc
            modal-class="hide-footer"
            hide-footer
            :hide-header-close="loading"
            @hidden="onHidden"
            @shown="setCustomerDetails"
        >
            <b-form
                @submit.stop.prevent="onSubmit()"
                @reset.stop.prevent="onHidden"
            >
                <div class="row custom-model-form">
                    <div class="col-md-12">
                        <b-form-group
                            id="input-group-1"
                            label="KYC Update Date"
                            label-for="input-1"
                        >
                            <date-time-picker
                                v-model="dateUpdate"
                                disabled="disabled"
                                date-format="MMM D, YYYY h:mm a"
                                format-val="MMM D, YYYY h:mm a"
                                placeholder="Select date time"
                                :custom-disable="true"
                            ></date-time-picker>
                        </b-form-group>

                        <div class="form-group">
                            <label>Risk Rating</label>
                            <tags
                                v-for="(list, index) in tags"
                                :key="index"
                                :status-type="list.actions"
                                :tag-icon="list.icon"
                                :tag-heading="list.headings"
                            >
                            </tags>
                        </div>

                        <b-form-group
                            id="input-group-4"
                            class="kyc-status-radio"
                            label="KYC Status"
                            label-for="kyc-status"
                        >
                            <b-form-group>
                                <b-form-radio-group
                                    v-model="formData.kycStatus"
                                    :options="statusOptions"
                                    name="status"
                                ></b-form-radio-group>
                            </b-form-group>
                        </b-form-group>

                        <div class="form-group">
                            <label for="kyc-status">KYC Comments</label>
                            <textarea
                                v-model="formData.comments"
                                v-validate="{
                                    required: true,
                                }"
                                name="comments"
                                class="form-control"
                                placeholder="Start typing your KYC Comments"
                                rows="3"
                                :class="{
                                    'is-invalid': errors.has('comments'),
                                }"
                            ></textarea>
                        </div>
                        <base-upload-file
                            type="customers"
                            :label-val="'Attach Document'"
                            @set-value="setFileName"
                        ></base-upload-file>
                    </div>
                </div>
                <div class="col-md-12 action-buttons">
                    <base-button
                        btn-label="Submit"
                        btn-type="submit"
                        btn-variant="primary"
                        :class="loading || fileLoading ? 'disabled' : ''"
                    ></base-button>
                    <base-button
                        btn-label="Cancel"
                        btn-type="reset"
                        :disabled="loading"
                        btn-variant="link"
                    ></base-button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import tags from '@/components/Tags.vue'
import dateTimePicker from '@/components/DateTimePicker.vue'
import { mapGetters } from 'vuex'
import resource from '@/api/resource'

export default {
    /*
    |--------------------------------------------------------------------------
    | Component > components
    |--------------------------------------------------------------------------
    */
    components: {
        tags,
        dateTimePicker,
    }, // End of Component > components

    /*
    |--------------------------------------------------------------------------
    | Component > computed
    |--------------------------------------------------------------------------
    */
    computed: {
        ...mapGetters([
            'loading',
            'dataSegmentId',
            'kycRole',
            'alertRecord',
            'hidePopup',
            'customerRecord',
            'fileLoading',
        ]),
    }, // End of Component > computed

    /*
    |--------------------------------------------------------------------------
    | Component > watch
    |--------------------------------------------------------------------------
    */
    watch: {
        alertRecord(value) {
            if (value) {
                this.formData.id = value._id
            }
        },

        /**
         * Handle when the popup hide
         *
         * @return {void}
         */
        hidePopup(val) {
            if (val) {
                this.onHidden()
            }
        },
    }, // End of Component > watch

    /*
    |--------------------------------------------------------------------------
    | Component > data
    |--------------------------------------------------------------------------
    */
    data() {
        return {
            dateUpdate: this.$moment().format('MMM D, YYYY h:mm a'),
            formData: this.initializeFormData(),
            statusOptions: [],
            tags: [],
        }
    }, // End of Component > data

    /*
    |--------------------------------------------------------------------------
    | Component > mounted
    |--------------------------------------------------------------------------
    */
    mounted() {}, // End of Component > data

    /*
    |--------------------------------------------------------------------------
    | Component > methods
    |--------------------------------------------------------------------------
    */
    methods: {
        initializeFormData() {
            return {
                id: null,
                dataSegmentId: null,
                customerId: null,
                riskRating: null,
                kycStatus: 'pending',
                comments: '',
                file: {
                    fileName: null,
                    originalName: null,
                },
            }
        }, // End of initializeFormData() methods

        setFileName(fileObj) {
            this.formData.file.fileName = fileObj.fileName
            this.formData.file.originalName = fileObj.originalName
        }, // End of setFileName() methods

        async onSubmit() {
            const result = await this.$validator.validateAll()
            this.formData.dataSegmentId = localStorage.getItem(
                'activeDataSegmentId'
            )

            this.formData.customerId = this.$route.params.id
            this.formData.kycStatus =
                this.formData.kycStatus == 're-submit'
                    ? 'pending'
                    : this.formData.kycStatus
            if (result) {
                try {
                    this.$store.commit('setLoading', true)
                    if (!this.formData.id) {
                        this.formData.id = this.alertRecord._id
                    }
                    if (!this.formData.riskRating) {
                        this.formData.riskRating =
                            this.customerRecord.kycSummary.riskRating
                    }
                    if (this.formData.id) {
                        // initiate review process for this alert
                        let resourceApi = new resource(
                            'alerts/kyc-status-review'
                        )
                        let { data } = await resourceApi.update(
                            this.formData.id,
                            this.formData
                        )
                        if (data) {
                            await this.$store.commit(
                                'setAlertRecord',
                                data.data
                            )
                            let customer = JSON.parse(
                                JSON.stringify(this.customerRecord)
                            )
                            customer.nameScreeningStatus = 'locked'
                            this.$store.commit('setCustomerRecord', customer)

                            this.$store.commit('setRefreshProfile', true)
                            this.$store.commit('setSuccess', data.message)
                            if (this.formData.kycStatus == 'return-alert') {
                                this.$router.push({
                                    name: 'view-alerts',
                                })
                            } else {
                                await this.$router
                                    .push({
                                        name: 'customer-information',
                                        params: {
                                            id: data.data.customerId,
                                        },
                                    })
                                    .catch(() => {})
                            }
                        }
                        // this.$store.commit('setLoading', false)
                    }
                } catch (error) {
                    this.$store.commit('setLoading', false)
                    this.$store.commit('setError', error)
                    console.log(error, 'There is an error on submit')
                }
            }
        }, // End of onSubmit() method

        /**
         * Handle when the modal is closed
         *
         * @return {void}
         */
        onHidden() {
            // clear the selected item
            this.$store.commit('clearItem')
            Object.assign(this.formData, this.initializeFormData())
            this.tags = []
            this.statusOptions = []
            this.$validator.reset()
            this.$refs.modal.hide()
        }, // End of onHidden() method

        setCustomerDetails() {
            if (this.customerRecord && this.customerRecord.kycSummary) {
                this.statusOptions = [
                    { text: 'Pending Review', value: 'pending' },
                    { text: 'Accepted', value: 'approved' },
                    { text: 'Rejected', value: 'rejected' },
                    { text: 'Re-submit documents', value: 're-submit' },
                ]
                if (
                    localStorage.getItem('isLeastRole') &&
                    localStorage.getItem('isLeastRole') == 'false'
                ) {
                    this.statusOptions.push({
                        text: 'Return Alert',
                        value: 'return-alert',
                    })
                }
                this.tags.push({
                    headings: this.customerRecord.kycSummary.riskRating,
                    icon: 'icon-graph',
                    actions: this.customerRecord.kycSummary.riskRating,
                })
                this.formData.riskRating =
                    this.customerRecord.kycSummary.riskRating
            }
        }, // End of setCustomerDetails() method
    }, // End of Component > mounted
}
</script>
